import React, { Suspense, useMemo } from "react";
import { HashRouter, Route, Routes } from "react-router-dom";
import { useSelector } from "react-redux";
import "./scss/style.scss";
// 3C redesign — design tokens (global) + app shell (scoped under .app).
// Imported AFTER style.scss so the new base font/colour win over CoreUI.
import "./scss/3c-tokens.css";
import "./scss/3c-shell.css";
import "./scss/3c-inputs.css";
import "./scss/3c-ui.css";
import "./scss/3c-auth.css";

// routes config
import adminRoute from "src/routes/admin_route";
import retailerRoute from "src/routes/retailer_route";
import { isStaffRole } from "src/utils/constant_user_role";
import { makeCan } from "src/utils/constant_permission";
import { getLoggedIn, getPermissions, getPermissionsLoaded } from "src/reducers/reducer_core";

const loading = (
    <div className="pt-3 text-center">
        <div className="sk-spinner sk-spinner-pulse"></div>
    </div>
);

// Containers
const DefaultLayout = React.lazy(() => import("./layout/DefaultLayout"));

// Pages
const Logout = React.lazy(() => import("./pages/logout/Logout"));
const Login = React.lazy(() => import("./pages/login/Login"));
const Register = React.lazy(() => import("./pages/register/Register"));
const Page404 = React.lazy(() => import("./pages/page404/Page404"));
const Page500 = React.lazy(() => import("./pages/page500/Page500"));
const NoAccess = React.lazy(() => import("./pages/no_access/NoAccess"));
const ForgotPassword = React.lazy(() => import("./pages/forgot_password/ForgotPassword"));
const ForgotPasswordSent = React.lazy(() => import("./pages/forgot_password_sent/ForgotPasswordSent"));
const ResetPassword = React.lazy(() => import("./pages/reset_password/ResetPassword"));
const ResetPasswordSuccess = React.lazy(() => import("./pages/reset_password_success/ResetPasswordSuccess"));

const App = () => {
    const loggedIn = useSelector(getLoggedIn);
    const permissions = useSelector(getPermissions);
    const permissionsLoaded = useSelector(getPermissionsLoaded);

    const isStaff = isStaffRole(loggedIn?.users);

    // Until /admin/auth/me has answered, mount NOTHING permission-gated. This is
    // what makes a stale persisted session fail closed: a blob saved before the
    // permission model has no permissionsLoaded flag, so it rehydrates falsy and
    // the sidebar and routes stay empty for one round trip rather than briefly
    // showing a Store Keeper everything.
    const staffRoutes = useMemo(() => {
        if (!permissionsLoaded) return [];
        const can = makeCan(permissions);
        return adminRoute.filter((route) => !route.perm || can(route.perm));
    }, [permissionsLoaded, permissions]);

    return (
        <HashRouter>
            <Suspense fallback={loading}>
                <Routes>
                    <Route exact path="/logout" name="Login Page" element={<Logout />} />
                    <Route exact path="/login" name="Login Page" element={<Login />} />
                    <Route exact path="/register" name="Register Page" element={<Register />} />
                    <Route exact path="/404" name="Page 404" element={<Page404 />} />
                    <Route exact path="/500" name="Page 500" element={<Page500 />} />
                    <Route exact path="/forgot-password" name="Forgot Password" element={<ForgotPassword />} />
                    <Route exact path="/forgot-password-sent" name="Forgot Password Sent" element={<ForgotPasswordSent />} />
                    <Route exact path="/reset-password/:token" name="Reset Password" element={<ResetPassword />} />
                    <Route exact path="/reset-password-success" name="Reset Password Success" element={<ResetPasswordSuccess />} />
                    <Route path="dashboard" name="Dashboard" element={<DefaultLayout />}>
                        {isStaff &&
                            staffRoutes.map((route, idx) => {
                                return route.element && <Route key={idx} path={route.path} name={route.name} element={<route.element />} />;
                        })}

                        {loggedIn?.users && !isStaff &&
                            retailerRoute.map((route, idx) => {
                                return route.element && <Route key={idx} path={route.path} name={route.name} element={<route.element />} />;
                        })}

                        {/* Anything under /dashboard this role cannot open. Rendered
                            INSIDE DefaultLayout so the sidebar stays available — the
                            old behaviour fell through to a layout-less 404 whose only
                            way out was a link to "/". While permissions are still
                            loading the same slot shows a spinner rather than a false
                            "no access". */}
                        <Route path="*" element={<NoAccess loading={isStaff && !permissionsLoaded} />} />
                    </Route>
                    <Route path="/" name="Dashboard" element={<DefaultLayout />} />
                    <Route path="*" name="Not Found" element={<Page404 />} />
                </Routes>
            </Suspense>
        </HashRouter>
    );
};

export default App;
